// Dynamic deadline system for migration slots
export const getDeadlineInfo = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  
  // Define deadline dates for current year
  const deadlines = [
    new Date(currentYear, 10, 30), // November 30
    new Date(currentYear, 11, 15), // December 15
    new Date(currentYear, 11, 31), // December 31
    new Date(currentYear + 1, 0, 10) // January 10 next year
  ];
  
  // Find the next upcoming deadline
  const nextDeadline = deadlines.find(deadline => deadline > now);
  
  if (!nextDeadline) {
    // If all deadlines have passed, use next year's first deadline
    return {
      deadline: new Date(currentYear + 1, 10, 30),
      daysLeft: Math.ceil((new Date(currentYear + 1, 10, 30) - now) / (1000 * 60 * 60 * 24)),
      isUrgent: false,
      message: "Book your migration slot for next year"
    };
  }
  
  const daysLeft = Math.ceil((nextDeadline - now) / (1000 * 60 * 60 * 24));
  
  return {
    deadline: nextDeadline,
    daysLeft: daysLeft,
    isUrgent: daysLeft <= 7,
    message: daysLeft <= 3 
      ? `Only ${daysLeft} days left to secure your migration slot!`
      : daysLeft <= 7
      ? `${daysLeft} days left to book your migration slot`
      : `Book your migration slot by ${nextDeadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}`
  };
};

export const getUrgencyMessage = () => {
  const { daysLeft, isUrgent, message } = getDeadlineInfo();
  
  if (daysLeft <= 1) {
    return "FINAL DAY - Book your migration slot now!";
  } else if (daysLeft <= 3) {
    return `Only ${daysLeft} days left - Secure your slot today!`;
  } else if (daysLeft <= 7) {
    return `${daysLeft} days left to book your migration slot`;
  } else {
    return message;
  }
};

// Cookie management for consistent slot tracking
const SLOT_COOKIE_NAME = 'buddy_migration_slots';
const SLOT_COOKIE_EXPIRY_DAYS = 7;

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getInitialSlotsForDeadline = (daysLeft) => {
  // Base slot calculation based on urgency
  if (daysLeft <= 1) {
    return Math.floor(Math.random() * 3) + 1; // 1-3 slots
  } else if (daysLeft <= 3) {
    return Math.floor(Math.random() * 5) + 2; // 2-6 slots
  } else if (daysLeft <= 7) {
    return Math.floor(Math.random() * 8) + 5; // 5-12 slots
  } else {
    return Math.floor(Math.random() * 15) + 10; // 10-24 slots
  }
};

export const getSlotsLeft = () => {
  const { daysLeft, deadline } = getDeadlineInfo();
  const deadlineKey = deadline.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  // Try to get existing slot data from cookie
  const cookieData = getCookie(SLOT_COOKIE_NAME);
  let slotData = {};
  
  if (cookieData) {
    try {
      slotData = JSON.parse(cookieData);
    } catch (e) {
      slotData = {};
    }
  }
  
  // Check if we have data for this deadline
  if (slotData[deadlineKey]) {
    const { slots, lastSeen, lastDecrease } = slotData[deadlineKey];
    const now = new Date();
    const hoursSinceLastSeen = (now - new Date(lastSeen)) / (1000 * 60 * 60);
    const hoursSinceLastDecrease = (now - new Date(lastDecrease)) / (1000 * 60 * 60);
    
    // Decrease slots if user returns after some time (but never below 2)
    let newSlots = slots;
    if (hoursSinceLastSeen > 2 && hoursSinceLastDecrease > 4) {
      // Decrease by 1-3 slots, but never below 2
      const decreaseAmount = Math.min(Math.floor(Math.random() * 3) + 1, slots - 2);
      newSlots = Math.max(2, slots - decreaseAmount);
      
      // Update the data
      slotData[deadlineKey] = {
        slots: newSlots,
        lastSeen: now.toISOString(),
        lastDecrease: now.toISOString()
      };
      
      // Save to cookie
      setCookie(SLOT_COOKIE_NAME, JSON.stringify(slotData), SLOT_COOKIE_EXPIRY_DAYS);
    } else {
      // Just update last seen time
      slotData[deadlineKey] = {
        ...slotData[deadlineKey],
        lastSeen: now.toISOString()
      };
      
      // Save to cookie
      setCookie(SLOT_COOKIE_NAME, JSON.stringify(slotData), SLOT_COOKIE_EXPIRY_DAYS);
    }
    
    return newSlots;
  } else {
    // First time seeing this deadline, generate initial slots
    const initialSlots = getInitialSlotsForDeadline(daysLeft);
    const now = new Date();
    
    slotData[deadlineKey] = {
      slots: initialSlots,
      lastSeen: now.toISOString(),
      lastDecrease: now.toISOString()
    };
    
    // Save to cookie
    setCookie(SLOT_COOKIE_NAME, JSON.stringify(slotData), SLOT_COOKIE_EXPIRY_DAYS);
    
    return initialSlots;
  }
};

// Function to decrease slots when someone books (simulate slot being taken)
export const decreaseSlots = () => {
  const { deadline } = getDeadlineInfo();
  const deadlineKey = deadline.toISOString().split('T')[0];
  
  const cookieData = getCookie(SLOT_COOKIE_NAME);
  let slotData = {};
  
  if (cookieData) {
    try {
      slotData = JSON.parse(cookieData);
    } catch (e) {
      slotData = {};
    }
  }
  
  if (slotData[deadlineKey] && slotData[deadlineKey].slots > 2) {
    slotData[deadlineKey].slots = Math.max(2, slotData[deadlineKey].slots - 1);
    slotData[deadlineKey].lastDecrease = new Date().toISOString();
    
    setCookie(SLOT_COOKIE_NAME, JSON.stringify(slotData), SLOT_COOKIE_EXPIRY_DAYS);
  }
  
  return slotData[deadlineKey] ? slotData[deadlineKey].slots : getSlotsLeft();
};

// Dynamic wording based on current date
export const getMigrationWording = () => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11 (Jan = 0, Dec = 11)
  const currentYear = now.getFullYear();
  
  // Check if we're between October (9) and January (0) of next year
  const isOctoberToJanuary = currentMonth >= 9 || currentMonth <= 0;
  
  if (isOctoberToJanuary) {
    // For Oct 15 - Jan 15, show "for January 202X"
    const targetYear = currentMonth <= 0 ? currentYear : currentYear + 1;
    return `for January ${targetYear}`;
  } else {
    // For other months, show "this quarter"
    return "this quarter";
  }
};
